import { BsList, BsBellFill, BsX } from "react-icons/bs";
import { useState, useEffect, useRef } from "react";
import Logo from "../assets/header-logo-dark.png";
import { Link } from "react-router-dom";
import { getAvatarByUserId } from "../utils/tools";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";

const navigationNormal = [
  { name: "Events", href: "/events", current: false },
  { name: "Practitioners", href: "/practitioners", current: false },
  { name: "Blog", href: "/blog", current: false },
  // { name: 'Submit', href: '/submit', current: false },
  { name: "About", href: "/about", current: false },
];

const navigationHoldingSite = [
  { name: "About", href: "/about", current: false },
  { name: "Blog", href: "/blog", current: false },
];

const navigation =
  process.env.REACT_APP_ENVIRONMENT !== "production"
    ? navigationNormal
    : navigationHoldingSite;

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export default function NavBar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { providerData } = useData();
  const { currentUser, logout } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const avatarImage = currentUser
    ? (() => {
        const userProvider = providerData.find(
          (provider) => provider.owner_id === currentUser.uid
        );
        if (userProvider?.provider_logo) {
          return userProvider.provider_logo;
        }
        return `/images/placeholder_avatars/${getAvatarByUserId(
          currentUser.uid
        )}`;
      })()
    : undefined;

  return (
    <nav className="bg-muted-lightGrey text-muted-black border-b border-muted-black">
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between">
          {/* Mobile menu button */}
          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative inline-flex items-center justify-center rounded-md p-2 text-muted-black hover:bg-muted-grey/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <BsX className="block size-6" aria-hidden="true" />
              ) : (
                <BsList className="block size-6" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Logo and Navigation */}
          <div className="flex flex-1 items-center justify-between sm:items-stretch">
            <div className="flex flex-1 shrink-0 items-center justify-center sm:justify-start">
              <Link to="/" className="flex items-center gap-2">
                <img className="h-8 w-auto" src={Logo} alt="logo" />
                <span className="text-muted-black text-lg tracking-wider uppercase">
                  Soundness
                </span>
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:block">
              <div className="flex space-x-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={classNames(
                      item.current
                        ? "text-muted-black underline decoration-muted-black"
                        : "text-muted-black no-underline hover:underline hover:decoration-muted-grey",
                      "px-3 py-2 text-sm font-normal"
                    )}
                    aria-current={item.current ? "page" : undefined}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Right side buttons */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            {/* <button
              type="button"
              className="relative rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
            >
              <span className="absolute -inset-1.5" />
              <span className="sr-only">View notifications</span>
              <BsBellFill className="size-6" aria-hidden="true" />
            </button> */}

            {/* Profile dropdown */}
            {/* TODO: remove this once we launch the site on production */}
            {process.env.REACT_APP_ENVIRONMENT !== "production" && (
              <div>
                {currentUser ? (
                  <div className="relative ml-3" ref={menuRef}>
                    <button
                      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                      className="relative flex rounded-full bg-muted-lightGrey text-sm focus:outline-none focus:ring-2 focus:ring-muted-grey focus:ring-offset-2 focus:ring-offset-muted-lightGrey"
                    >
                      <span className="absolute -inset-1.5" />
                      <span className="sr-only">Open user menu</span>
                      <img
                        className="size-8 rounded-full border border-white"
                        src={avatarImage}
                        alt="User avatar"
                      />
                    </button>

                    <div
                      className={`absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-muted-lightGrey border border-muted-grey hover:shadow-[10px_10px_0_-2px_theme(colors.muted.grey)] transition-all duration-300 ease-in-out transform ${
                        isProfileMenuOpen
                          ? "opacity-100 scale-100 translate-y-0"
                          : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                      }`}
                    >
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-muted-black hover:bg-white transition-all duration-300"
                      >
                        Your Profile
                      </Link>
                      <div
                        onClick={() => logout()}
                        className="block px-4 py-2 text-sm text-muted-black hover:bg-white transition-all duration-300"
                      >
                        Sign out
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="inline-block bg-muted-lavender/80  py-2 px-3 border border-muted-darkGrey rounded-[10px] shadow-[5px_5px_0_-2px_theme(colors.muted.black)] hover:bg-muted-lavender/40 hover:shadow-[10px_10px_0_-2px_theme(colors.muted.black)] transition-all text-sm"
                  >
                    Login
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`sm:hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen
            ? "max-h-64 opacity-100"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="space-y-1 px-2 pb-3 pt-2">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={classNames(
                item.current
                  ? "text-muted-black underline decoration-muted-black"
                  : "text-muted-black no-underline hover:underline hover:decoration-muted-grey",
                "block px-3 py-2 text-base font-normal"
              )}
              aria-current={item.current ? "page" : undefined}
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}
